/**
 * The panels the tool rail opens. §6.2.
 *
 * Canva's arrangement: a rail of icons down the left, each opening a panel of
 * things to add - Design, Elements, Text, Uploads, Draw - with a search at the
 * top where there is enough to search. The customer picks what they want and
 * it lands on the page, rather than a click on the rail putting something
 * down before they have seen what else there is.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { takeTemplate } from '../templates';
import { api, type Graphics, type Icons, type Template } from '../api';
import { useEditor } from '../state/store';
import type { TrimboxDocument } from '../model/types';
import { buildPayload, type QrFieldValues, type QrKind } from '../codes/qr-types';
import { QrFields, QrKindSelect } from './QrFields';
import { addGraphic, addQr, addShape, addTextCombo, addTextStyle, placeAsset, type TextStyle } from '../state/actions';
import { shapeGroups } from '../shapes/library';
import { fontFamilies } from '../text/fonts';
import { allAssets, onAssetsChanged } from '../assets';
import { Preview } from './ShapePicker';
import { addFrame, FRAME_SHAPES } from '../state/masks';
import { shapeDef } from '../shapes/library';
import { cssName, showFace } from './FontField';

function Shell( {
  title,
  onClose,
  search,
  children,
}: {
  title: string;
  onClose: () => void;
  search?: { value: string; onChange: ( value: string ) => void; placeholder: string };
  children: ReactNode;
} ) {
  return (
    <aside className="tb-side tb-rp" aria-label={ title }>
      <div className="tb-side__head">
        <h2 className="tb-side__title">{ title }</h2>
        <button type="button" className="tb-btn tb-btn--quiet tb-btn--icon" aria-label={ `Close ${ title.toLowerCase() }` } onClick={ onClose }>
          ✕
        </button>
      </div>

      { search && (
        <div className="tb-rp__search">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <circle cx="9" cy="9" r="6" />
            <path d="M14 14l4 4" />
          </svg>
          <input
            className="tb-rp__field"
            type="search"
            value={ search.value }
            placeholder={ search.placeholder }
            aria-label={ search.placeholder }
            onChange={ ( event ) => search.onChange( event.target.value ) }
          />
        </div>
      ) }

      <div className="tb-rp__body">{ children }</div>
    </aside>
  );
}

/**
 * One shelf of the panel.
 *
 * Given `open` and `onToggle` it becomes an accordion instead - for the shelves
 * whose contents come from the shop rather than from the app, and which should
 * not be fetched, drawn and scrolled past by every customer who only wanted a
 * rectangle.
 */
function Section( {
  title,
  note,
  open,
  onToggle,
  children,
}: {
  title: string;
  note?: string;
  open?: boolean;
  onToggle?: () => void;
  children: ReactNode;
} ) {
  if ( ! onToggle ) {
    return (
      <section className="tb-rp__section">
        <h3 className="tb-rp__heading">{ title }</h3>
        { children }
      </section>
    );
  }

  return (
    <section className="tb-rp__section">
      <h3 className="tb-rp__heading tb-rp__heading--fold">
        <button type="button" className="tb-rp__fold" aria-expanded={ !! open } onClick={ onToggle }>
          <svg
            className={ `tb-rp__chev${ open ? ' is-open' : '' }` }
            width="12"
            height="12"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            aria-hidden="true"
          >
            <path d="M7 4l7 6-7 6" />
          </svg>
          <span>{ title }</span>
          { note && <small className="tb-rp__note-inline">{ note }</small> }
        </button>
      </h3>

      { open && children }
    </section>
  );
}

/* ---------------------------------------------------------------
 * Design: this product's templates
 * ------------------------------------------------------------ */

export function DesignPanel( { found, onPick, onClose }: { found: Template[]; onPick: ( doc: TrimboxDocument ) => void; onClose: () => void } ) {
  const [ search, setSearch ] = useState( '' );
  const [ taking, setTaking ] = useState( '' );
  const [ problem, setProblem ] = useState( '' );

  const shown = found.filter( ( t ) => t.title.toLowerCase().includes( search.trim().toLowerCase() ) );

  const take = async ( template: Template ) => {
    setTaking( template.uuid );
    setProblem( '' );

    try {
      const current = useEditor.getState().doc;

      if ( current ) await takeTemplate( template.uuid, onPick, current );
    } catch {
      setProblem( 'That template could not be opened. Please try another.' );
    }

    setTaking( '' );
  };

  return (
    <Shell title="Design" onClose={ onClose } search={ found.length > 3 ? { value: search, onChange: setSearch, placeholder: 'Search templates' } : undefined }>
      <Section title="Templates">
        { problem && <p className="tb-rp__problem">{ problem }</p> }
        { found.length === 0 && <p className="tb-rp__note">This product has no templates yet. Start from a blank card, or add text and shapes from the left.</p> }
        { found.length > 0 && <p className="tb-rp__note">Picking one replaces everything on the card. Undo puts it back.</p> }

        <div className="tb-rp__cards">
          { shown.map( ( template ) => (
            <button
              key={ template.uuid }
              type="button"
              className="tb-rp__card"
              disabled={ taking !== '' }
              onClick={ () => void take( template ) }
              title={ template.title }
            >
              <span className="tb-rp__shot">
                { template.thumb ? <img src={ template.thumb } alt="" loading="lazy" /> : <span className="tb-rp__blank" /> }
              </span>
              <span className="tb-rp__caption">{ taking === template.uuid ? 'Opening…' : template.title }</span>
            </button>
          ) ) }
        </div>
      </Section>
    </Shell>
  );
}

/* ---------------------------------------------------------------
 * Elements: shapes, lines, codes, drawing
 * ------------------------------------------------------------ */

export function ElementsPanel( {
  onDraw,
  onClose,
  onProblem,
}: {
  onDraw: ( which: 'pen' | 'pencil' ) => void;
  onClose: () => void;
  /** A drawing the shop's library could not give us, said in words. */
  onProblem?: ( said: string ) => void;
} ) {
  const [ search, setSearch ] = useState( '' );
  const needle = search.trim().toLowerCase();

  const groups = shapeGroups()
    .map( ( { group, shapes } ) => ( {
      group,
      shapes: shapes.filter( ( s ) => ! needle || s.name.toLowerCase().includes( needle ) || group.toLowerCase().includes( needle ) ),
    } ) )
    .filter( ( g ) => g.shapes.length > 0 );

  const wants = ( words: string ) => ! needle || words.includes( needle );

  return (
    <Shell title="Elements" onClose={ onClose } search={ { value: search, onChange: setSearch, placeholder: 'Search shapes and elements' } }>
      { wants( 'draw pen pencil freehand line' ) && (
        <Section title="Draw your own">
          <div className="tb-rp__tools">
            <button type="button" className="tb-rp__tool" onClick={ () => onDraw( 'pen' ) }>
              <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M3 17c0-6 4-11 9-13" />
                <path d="M12 2.5l4.5 2-2 4.5-4.5-2z" />
              </svg>
              <span>Pen</span>
              <small>Points and curves</small>
            </button>
            <button type="button" className="tb-rp__tool" onClick={ () => onDraw( 'pencil' ) }>
              <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M13.5 3.5l3 3L7 16l-4 1 1-4z" />
              </svg>
              <span>Pencil</span>
              <small>Freehand</small>
            </button>
          </div>
        </Section>
      ) }

      { wants( 'qr code link website' ) && (
        <Section title="Codes">
          <QrTool />
        </Section>
      ) }

      { wants( 'frames frame picture photo mask' ) && (
        <Section title="Frames" note="Add one, then choose a picture to fill it">
          <div className="tb-rp__shapes tb-rp__frames">
            { FRAME_SHAPES.map( ( id ) => (
              <button
                key={ id }
                type="button"
                className="tb-rp__shape tb-rp__frame"
                title={ `${ shapeDef( id ).name } frame` }
                aria-label={ `${ shapeDef( id ).name } frame` }
                data-frame={ id }
                onClick={ () => addFrame( id ) }
              >
                <Preview shape={ shapeDef( id ) } />
              </button>
            ) ) }
          </div>
        </Section>
      ) }

      <IconsSection needle={ needle } onProblem={ onProblem } />

      <GraphicsSection needle={ needle } onProblem={ onProblem } />

      { groups.map( ( { group, shapes } ) => (
        <Section key={ group } title={ group === 'Basic' ? 'Shapes' : group }>
          <div className="tb-rp__shapes">
            { shapes.map( ( shape ) => (
              <button key={ shape.id } type="button" className="tb-rp__shape" title={ shape.name } aria-label={ shape.name } onClick={ () => addShape( shape.id ) }>
                <Preview shape={ shape } />
              </button>
            ) ) }
          </div>
        </Section>
      ) ) }

      { needle && groups.length === 0 && ! wants( 'draw pen pencil freehand line qr code link website' ) && (
        <p className="tb-rp__note">Nothing called that. Try "star", "arrow" or "line".</p>
      ) }
    </Shell>
  );
}

/* ---------------------------------------------------------------
 * Text: styles and pairings
 * ------------------------------------------------------------ */

/** Pairings, each only offered when the shop has both its fonts. */
const COMBOS: Array<{ name: string; lines: TextStyle[] }> = [
  { name: 'Opening', lines: [ { text: 'GRAND OPENING', size: 30, family: 'Bebas Neue', letterSpacing: 0.3 }, { text: 'Doha · 12 March', size: 11, family: 'Lato' } ] },
  { name: 'Elegant', lines: [ { text: 'Elegant Events', size: 24, family: 'Playfair Display', weight: 700 }, { text: 'PLANNING & DÉCOR', size: 9, family: 'Montserrat', letterSpacing: 0.4 } ] },
  { name: 'Studio', lines: [ { text: 'BOLD IDEAS', size: 26, family: 'Montserrat', weight: 800 }, { text: 'Creative studio', size: 11, family: 'Open Sans' } ] },
  { name: 'Bakery', lines: [ { text: 'Sweet Treats', size: 28, family: 'Lobster Two', weight: 700 }, { text: 'Bakery & café', size: 11, family: 'Raleway' } ] },
  { name: 'Name', lines: [ { text: 'Jino Joseph', size: 20, family: 'Poppins', weight: 600 }, { text: 'Creative Director', size: 10, family: 'Poppins' } ] },
  { name: 'Arabic', lines: [ { text: 'بريزمات للطباعة', size: 24, family: 'Cairo', weight: 700 }, { text: 'طباعة احترافية', size: 12, family: 'Cairo' } ] },
];

export function TextPanel( { onClose }: { onClose: () => void } ) {
  const families = fontFamilies();
  const combos = COMBOS.filter( ( c ) => c.lines.every( ( l ) => ! l.family || families.includes( l.family ) ) );
  const first = families[ 0 ] ?? 'Lato';

  // The pairings are shown in their own fonts; those arrive as they are needed.
  const [ , redraw ] = useState( 0 );

  useEffect( () => {
    const wanted = Array.from( new Set( combos.flatMap( ( c ) => c.lines.map( ( l ) => l.family ?? first ) ) ) );

    void Promise.all( wanted.map( showFace ) ).then( () => redraw( ( n ) => n + 1 ) );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [] );

  return (
    <Shell title="Text" onClose={ onClose }>
      <div className="tb-rp__section">
        <button type="button" className="tb-btn tb-btn--primary tb-rp__wide" onClick={ () => addTextStyle( { text: 'Your text here', size: 14 } ) }>
          <span aria-hidden="true">T</span> Add a text box
        </button>
      </div>

      <Section title="Default text styles">
        <div className="tb-rp__styles">
          <button type="button" className="tb-rp__style tb-rp__style--h1" onClick={ () => addTextStyle( { text: 'Add a heading', size: 28, weight: 700 }, 'Add a heading' ) }>
            Add a heading
          </button>
          <button type="button" className="tb-rp__style tb-rp__style--h2" onClick={ () => addTextStyle( { text: 'Add a subheading', size: 16, weight: 600 }, 'Add a subheading' ) }>
            Add a subheading
          </button>
          <button type="button" className="tb-rp__style tb-rp__style--body" onClick={ () => addTextStyle( { text: 'Add a little bit of body text', size: 10 }, 'Add body text' ) }>
            Add a little bit of body text
          </button>
        </div>
      </Section>

      { combos.length > 0 && (
        <Section title="Font combinations">
          <div className="tb-rp__combos">
            { combos.map( ( combo ) => (
              <button key={ combo.name } type="button" className="tb-rp__combo" title={ combo.lines.map( ( l ) => l.family ).join( ' + ' ) } onClick={ () => addTextCombo( combo.lines ) }>
                { combo.lines.map( ( line, index ) => (
                  <span
                    key={ index }
                    style={ {
                      fontFamily: cssName( line.family ?? first ),
                      fontWeight: line.weight ?? 400,
                      fontSize: index === 0 ? 17 : 10,
                      letterSpacing: line.letterSpacing ? `${ line.letterSpacing * 0.2 }em` : undefined,
                    } }
                  >
                    { line.text }
                  </span>
                ) ) }
              </button>
            ) ) }
          </div>
        </Section>
      ) }
    </Shell>
  );
}

/* ---------------------------------------------------------------
 * Uploads: your pictures
 * ------------------------------------------------------------ */

export function UploadsPanel( { onUpload, uploading, onClose }: { onUpload: () => void; uploading: boolean; onClose: () => void } ) {
  const [ , redraw ] = useState( 0 );

  useEffect( () => onAssetsChanged( () => redraw( ( n ) => n + 1 ) ), [] );

  const pictures = allAssets();

  return (
    <Shell title="Uploads" onClose={ onClose }>
      <div className="tb-rp__section">
        <button type="button" className="tb-btn tb-btn--primary tb-rp__wide" disabled={ uploading } onClick={ onUpload }>
          { uploading ? 'Uploading…' : 'Upload files' }
        </button>
        <p className="tb-rp__note">JPG, PNG or WebP. For print, a logo or photo wants at least 300 pixels for every inch it is printed at.</p>
      </div>

      <Section title={ pictures.length ? 'Your pictures' : 'Nothing uploaded yet' }>
        <div className="tb-rp__pictures">
          { pictures.map( ( asset ) => (
            <button key={ asset.id } type="button" className="tb-rp__picture" title={ `${ asset.name } · ${ asset.w } × ${ asset.h } px` } onClick={ () => placeAsset( asset ) }>
              <img src={ asset.url } alt={ asset.name } loading="lazy" />
            </button>
          ) ) }
        </div>
      </Section>
    </Shell>
  );
}

/* ---------------------------------------------------------------
 * Draw
 * ------------------------------------------------------------ */

export function DrawPanel( { mode, onDraw, onClose }: { mode: 'select' | 'pen' | 'pencil'; onDraw: ( which: 'pen' | 'pencil' ) => void; onClose: () => void } ) {
  const tips = useMemo(
    () =>
      mode === 'pen'
        ? [ 'Click to place corners.', 'Click and drag for a curve.', 'Click the first point to close the shape.', 'Enter finishes an open line; Escape gives it up.' ]
        : mode === 'pencil'
          ? [ 'Press and draw.', 'Finish where you started to close the shape.', 'Your line is smoothed into curves you can edit.' ]
          : [ 'Choose a tool, then draw on the page.', 'Double-click a drawing afterwards to move its points.' ],
    [ mode ]
  );

  return (
    <Shell title="Draw" onClose={ onClose }>
      <div className="tb-rp__section">
        <div className="tb-rp__tools">
          <button type="button" className={ `tb-rp__tool${ mode === 'pen' ? ' is-on' : '' }` } aria-pressed={ mode === 'pen' } onClick={ () => onDraw( 'pen' ) }>
            <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M3 17c0-6 4-11 9-13" />
              <path d="M12 2.5l4.5 2-2 4.5-4.5-2z" />
            </svg>
            <span>Pen</span>
            <small>P</small>
          </button>
          <button type="button" className={ `tb-rp__tool${ mode === 'pencil' ? ' is-on' : '' }` } aria-pressed={ mode === 'pencil' } onClick={ () => onDraw( 'pencil' ) }>
            <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M13.5 3.5l3 3L7 16l-4 1 1-4z" />
            </svg>
            <span>Pencil</span>
            <small>N</small>
          </button>
        </div>
      </div>

      <Section title={ mode === 'select' ? 'How it works' : mode === 'pen' ? 'Drawing with the pen' : 'Drawing freehand' }>
        <ul className="tb-rp__tips">
          { tips.map( ( tip ) => (
            <li key={ tip }>{ tip }</li>
          ) ) }
        </ul>
      </Section>
    </Shell>
  );
}

/* ---------------------------------------------------------------
 * The shop's own drawings
 * ------------------------------------------------------------ */

/**
 * Cliparts, icons and flourishes the shop keeps, in their sections.
 *
 * Fetched once when the panel first opens, because a shop's library is a few
 * hundred small files and asking again on every keystroke would be rude to a
 * server that is also rendering press files.
 */
function GraphicsSection( { needle, onProblem }: { needle: string; onProblem?: ( said: string ) => void } ) {
  const [ library, setLibrary ] = useState<Graphics | null>( null );
  const [ section, setSection ] = useState( '' );
  const [ placing, setPlacing ] = useState( 0 );

  useEffect( () => {
    let cancelled = false;

    api
      .graphics()
      .then( ( found ) => {
        if ( ! cancelled ) setLibrary( found );
      } )
      .catch( () => {
        if ( ! cancelled ) setLibrary( { categories: [], items: [] } );
      } );

    return () => {
      cancelled = true;
    };
  }, [] );

  if ( ! library || library.items.length === 0 ) return null;

  const shown = library.items.filter( ( item ) => {
    const inSection = ! section || item.cats.includes( section );
    const matches = ! needle || item.name.toLowerCase().includes( needle ) || item.cats.some( ( cat ) => cat.includes( needle ) );

    return inSection && matches;
  } );

  if ( shown.length === 0 ) return null;

  const place = async ( item: Graphics[ 'items' ][ number ] ) => {
    setPlacing( item.id );

    const trouble = await addGraphic( item );

    setPlacing( 0 );

    if ( trouble ) onProblem?.( trouble );
  };

  return (
    <Section title="Graphics">
      { library.categories.length > 1 && (
        <div className="tb-rp__cats" role="tablist" aria-label="Graphic sections">
          <button
            type="button"
            role="tab"
            aria-selected={ '' === section }
            className={ `tb-rp__cat${ '' === section ? ' is-on' : '' }` }
            onClick={ () => setSection( '' ) }
          >
            All
          </button>

          { library.categories.map( ( cat ) => (
            <button
              key={ cat.slug }
              type="button"
              role="tab"
              aria-selected={ cat.slug === section }
              className={ `tb-rp__cat${ cat.slug === section ? ' is-on' : '' }` }
              onClick={ () => setSection( cat.slug ) }
            >
              { cat.name }
            </button>
          ) ) }
        </div>
      ) }

      <div className="tb-rp__shapes">
        { shown.slice( 0, 120 ).map( ( item ) => (
          <button
            key={ item.id }
            type="button"
            className="tb-rp__shape"
            title={ item.name }
            aria-label={ item.name }
            disabled={ placing !== 0 }
            onClick={ () => void place( item ) }
          >
            <img src={ item.thumb } alt="" loading="lazy" className="tb-rp__graphic" />
          </button>
        ) ) }
      </div>
    </Section>
  );
}

/* ---------------------------------------------------------------
 * A QR code, asked about before it is placed
 * ------------------------------------------------------------ */

/**
 * The QR tile, which asks what the code is for before adding it.
 *
 * It used to drop a code straight onto the card holding the shop's own address
 * as a placeholder. That is a code which can be laid out, approved on a proof
 * and printed while still pointing at the wrong place - and the first anyone
 * knows of it is a customer scanning a card and landing somewhere strange.
 *
 * So the tile opens into a small form instead. No dialog: the card stays
 * visible, which matters when the customer is deciding where the code goes.
 * The form is the same one the settings panel uses (QrFields), so a code is
 * asked the same questions whether it is being added or changed.
 */
function QrTool() {
  const [ open, setOpen ] = useState( false );
  const [ kind, setKind ] = useState<QrKind>( 'url' );
  const [ fields, setFields ] = useState<QrFieldValues>( {} );

  const ready = buildPayload( kind, fields ) !== '';

  const add = () => {
    if ( ! ready ) return;

    addQr( kind, fields );

    setFields( {} );
    setOpen( false );
  };

  if ( ! open ) {
    return (
      <div className="tb-rp__tools">
        <button type="button" className="tb-rp__tool" onClick={ () => setOpen( true ) }>
          <svg width="26" height="26" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <rect x="2" y="2" width="6" height="6" rx="1" />
            <rect x="12" y="2" width="6" height="6" rx="1" />
            <rect x="2" y="12" width="6" height="6" rx="1" />
            <rect x="12" y="12" width="2.5" height="2.5" />
            <rect x="15.5" y="15.5" width="2.5" height="2.5" />
          </svg>
          <span>QR code</span>
          <small>Links, contacts, WiFi and more</small>
        </button>
      </div>
    );
  }

  return (
    <div className="tb-rp__qr">
      <QrKindSelect value={ kind } onChange={ setKind } />

      <QrFields
        key={ kind }
        kind={ kind }
        fields={ fields }
        onChange={ setFields }
        onSubmit={ add }
        onCancel={ () => setOpen( false ) }
        autoFocus
      />

      <div className="tb-rp__qr-go">
        <button type="button" className="tb-btn tb-btn--primary" disabled={ ! ready } onClick={ add }>
          Add the code
        </button>

        <button type="button" className="tb-btn tb-btn--quiet" onClick={ () => setOpen( false ) }>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
 * The icon library
 * ------------------------------------------------------------ */

/**
 * Whether the icons shelf was left open.
 *
 * Module-level rather than in the store: it is a habit of this browsing
 * session, not part of the design, and it should not be one more thing that
 * makes a document dirty.
 */
let iconsWereOpen = false;

/**
 * The shop's icon library.
 *
 * A shelf that is shut until it is asked for. Everything on it comes from the
 * shop over the wire, and on a busy install that answer takes a few seconds:
 * open by default it arrived late, pushed the shapes down the panel as it came,
 * and made every customer wait for icons whether or not they wanted one. Shut,
 * it costs nothing until it is opened - and opening it is one click that says
 * what is behind it.
 *
 * Its own search box, because the panel's searches the shapes and the drawings
 * as well and a customer looking through icons is looking through icons. The
 * panel's search still reaches in here: a word typed at the top opens the shelf
 * and shows what matched, which is the only way it could find an icon behind a
 * closed door.
 */
function IconsSection( { needle, onProblem }: { needle: string; onProblem?: ( said: string ) => void } ) {
  const [ open, setOpen ] = useState( iconsWereOpen );
  const [ mine, setMine ] = useState( '' );
  const [ found, setFound ] = useState<Icons | null>( null );
  const [ category, setCategory ] = useState( '' );
  const [ page, setPage ] = useState( 1 );
  const [ placing, setPlacing ] = useState( '' );

  // What is actually being searched for: this shelf's own box if anything is
  // in it, otherwise whatever was typed at the top of the panel.
  const asked = mine.trim().toLowerCase() || needle;

  // A word typed at the top of the panel is a question about everything in it,
  // icons included, so it opens the shelf.
  const showing = open || '' !== needle;

  // Back to the first page whenever the question changes, or page three of an
  // old search becomes page three of a new one.
  useEffect( () => {
    setPage( 1 );
  }, [ asked, category ] );

  useEffect( () => {
    if ( ! showing ) return;

    let cancelled = false;

    /*
     * A moment's wait before asking. Typing "telephone" is nine keystrokes and
     * would otherwise be nine searches, of which only the last is wanted.
     */
    const timer = window.setTimeout( () => {
      api
        .icons( asked, category, page )
        .then( ( list ) => {
          if ( ! cancelled ) setFound( list );
        } )
        .catch( () => {
          if ( ! cancelled ) setFound( { items: [], categories: [], total: 0, page: 1, pages: 0 } );
        } );
    }, asked ? 180 : 0 );

    return () => {
      cancelled = true;
      window.clearTimeout( timer );
    };
  }, [ showing, asked, category, page ] );

  const place = async ( icon: Icons[ 'items' ][ number ] ) => {
    setPlacing( icon.id );

    const trouble = await addGraphic( { id: 0, name: icon.name, url: icon.url, kind: 'svg' } );

    setPlacing( '' );

    if ( trouble ) onProblem?.( trouble );
  };

  const toggle = () => {
    iconsWereOpen = ! showing;
    setOpen( iconsWereOpen );
  };

  return (
    <Section
      title="Icons"
      note={ showing && found ? `${ found.total }` : undefined }
      open={ showing }
      onToggle={ toggle }
    >
      <div className="tb-rp__search tb-rp__search--inline">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <circle cx="9" cy="9" r="6" />
          <path d="M14 14l4 4" />
        </svg>
        <input
          className="tb-rp__field tb-rp__field--icons"
          type="search"
          value={ mine }
          placeholder="Search icons"
          aria-label="Search icons"
          onChange={ ( event ) => setMine( event.target.value ) }
        />
      </div>

      { ! found && <p className="tb-rp__note">Looking…</p> }

      { found && found.categories.length > 1 && (
        <div className="tb-rp__cats" role="tablist" aria-label="Icon categories">
          <button
            type="button"
            role="tab"
            aria-selected={ '' === category }
            className={ `tb-rp__cat${ '' === category ? ' is-on' : '' }` }
            onClick={ () => setCategory( '' ) }
          >
            All
          </button>

          { found.categories.map( ( cat ) => (
            <button
              key={ cat.name }
              type="button"
              role="tab"
              aria-selected={ cat.name === category }
              className={ `tb-rp__cat${ cat.name === category ? ' is-on' : '' }` }
              onClick={ () => setCategory( cat.name ) }
            >
              { cat.name }
            </button>
          ) ) }
        </div>
      ) }

      { found && found.total === 0 && (
        <p className="tb-rp__note">
          { asked || category
            ? 'No icon here matches that. Try a plainer word — “phone”, “email”, “star”.'
            : 'The shop has no icons yet.' }
        </p>
      ) }

      <div className="tb-rp__shapes">
        { found?.items.map( ( icon ) => (
          <button
            key={ icon.id }
            type="button"
            className="tb-rp__shape"
            title={ icon.name }
            aria-label={ icon.name }
            disabled={ placing !== '' }
            onClick={ () => void place( icon ) }
          >
            <img src={ icon.url } alt="" loading="lazy" className="tb-rp__icon" />
          </button>
        ) ) }
      </div>

      { found && found.pages > 1 && (
        <div className="tb-rp__pager">
          <button type="button" className="tb-btn tb-btn--quiet" disabled={ page <= 1 } onClick={ () => setPage( page - 1 ) }>
            Back
          </button>

          <span className="tb-rp__count">
            { found.total } icons, page { found.page } of { found.pages }
          </span>

          <button type="button" className="tb-btn tb-btn--quiet" disabled={ page >= found.pages } onClick={ () => setPage( page + 1 ) }>
            More
          </button>
        </div>
      ) }
    </Section>
  );
}
